<template>
    <b-modal :active="show" @close="toggle" class="modal" has-modal-card>
      <div class="modal-content">
      <h2 class="title">Contact Truckl</h2>

        <div class="content">
          <p class="content">
            Please contact us, if you have questions, comments, feedback or ideas for improvement. We will get back to you as soon as we can.
          </p>
          <hr/>
          <form>
            <b-field label="Subject">
              <b-input type="text" v-model.trim="subject" />
            </b-field>
            <b-field label="You Message">
              <b-input type="textarea" v-model="body" />
            </b-field>
            <b-checkbox v-model="sendMeCopy" class="has-text-weight-semibold">
              Send me a copy of this message
            </b-checkbox>
          </form>
        </div>

        <div class="columns">
          <div class="column">
            <button type="button"
                    @click="sendEmail"
                    :disabled="isLoading"
                    :class="{ 'is-loading': isLoading }"
                    class="button is-success is-fullwidth">
              Send
            </button>

          </div>

          <div class="column is-one-third">
            <button type="button"
                    @click="toggle"
                    :disabled="isLoading"
                    class="button is-fullwidth">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </b-modal>
</template>

<script>
import { SES, CognitoIdentityCredentials } from 'aws-sdk'
import { mapState, mapGetters } from 'vuex'

const UserPoolId = process.env.VUE_APP_AWS_COGNITO_POOL_ID
const IdentityPoolId = process.env.VUE_APP_AWS_COGNITO_IDENTITY_POOL_ID

export default {
  name: 'ModalContactTruckl',

  props: {
    show: {
      type: Boolean,
      required: true,
      default: false
    }
  },

  created() {
    // TODO: when moving to prod needed to setup an Identity Pool in Cognito as well as the proper security policy
    this.creds = new CognitoIdentityCredentials(
      {
        IdentityPoolId: IdentityPoolId,
        Logins: {}
      },
      { region: 'us-east-1' }
    )
    this.creds.params.Logins[`cognito-idp.us-east-1.amazonaws.com/${UserPoolId}`] = this.tokens.idToken

    this.ses = new SES({
      region: 'us-east-1',
      credentials: this.creds
    })
  },

  data() {
    return {
      sendMeCopy: true,
      isLoading: false,
      creds: null,
      ses: null,
      subject: '',
      body: ''
    }
  },

  computed: {
    ...mapState('user', ['tokens']),
    ...mapGetters('user', ['accountInfo']),

    /**
     * AWS SES configuration parameters for the customer copay email
     * @returns {Object}
     */
    copyEmailParams() {
      const senderEmail = 'support@truckl.io'
      const senderName = 'Truckl Support'
      const toEmail = this.accountInfo.accountEmailAddress
      const emailBodyPrefix =
        'Thank you for contacting Truckl Support. As requested, here is a copy of your message: \n\n'
      const emailBodyText = `${emailBodyPrefix} ${this.body}`
      const emailBodyHtml = emailBodyText.replace(/\n/g, '<br/>')
      return {
        Destination: {
          // BccAddresses: [],
          // CcAddresses: [],
          ToAddresses: [toEmail]
        },
        ReplyToAddresses: [senderEmail],
        Message: {
          Body: {
            Html: {
              Charset: 'UTF-8',
              Data: emailBodyHtml
            },
            Text: {
              Charset: 'UTF-8',
              Data: emailBodyText
            }
          },
          Subject: {
            Charset: 'UTF-8',
            Data: this.subject
          }
        },
        // this needs to be sent from a domain verified by AWS. So we show the users name and use the real email address
        // in the "replyTo" field, but use a Truckl email so the message gets sent.
        Source: `${senderName} <support@truckl.io>`
        // SourceArn: ''
      }
    },

    /**
     * AWS SES configuration parameters for the support email
     * @returns {Object}
     */
    supportEmailParams() {
      const senderEmail = this.accountInfo.accountEmailAddress
      const senderName = `${this.accountInfo.accountFirstName} ${this.accountInfo.accountLastName}`
      const toEmail = 'support@truckl.io'
      const emailBody = this.body.replace(/\n/g, '<br/>')
      return {
        Destination: {
          // BccAddresses: [],
          // CcAddresses: [],
          ToAddresses: [toEmail]
        },
        ReplyToAddresses: [senderEmail],
        Message: {
          Body: {
            Html: {
              Charset: 'UTF-8',
              Data: emailBody
            },
            Text: {
              Charset: 'UTF-8',
              Data: emailBody
            }
          },
          Subject: {
            Charset: 'UTF-8',
            Data: `[from website] ${this.subject}`
          }
        },
        // this needs to be sent from a domain verified by AWS. So we show the users name and use the real email address
        // in the "replyTo" field, but use a Truckl email so the message gets sent.
        Source: `${senderName} <support@truckl.io>`
        // SourceArn: ''
      }
    }
  },

  methods: {
    async sendEmail() {
      try {
        this.isLoading = true
        await this.creds.refreshPromise()

        const supportEmail = this.ses.sendEmail(this.supportEmailParams).promise()
        let copyEmail = null
        if (this.sendMeCopy) {
          copyEmail = this.ses.sendEmail(this.copyEmailParams).promise()
        }

        await Promise.all([supportEmail, copyEmail])
      } catch (err) {
        console.error('Unable to send Contact Truckl email', err)
      } finally {
        console.log('Contact Truckl email was sent')
        this.isLoading = false
        // TODO: show a success message then close
        this.toggle()
      }
    },

    reset() {
      this.body = ''
      this.subject = ''
      this.sendMeCopy = true
    },

    toggle() {
      this.reset()
      this.$emit('update:show', false)
    }
  }
}
</script>

<style scoped lang="scss">
.modal-content {
  background-color: white;
  border-radius: 10px;
  padding: 15px;
  width: 750px;
}
</style>
